import { Link } from "react-router-dom";
import { ArrowRight, Clock, CheckCircle2, HomeIcon, Sparkles } from "lucide-react";
import { SERVICES } from "../data/mock";

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-[#3915AA]">
      {/* Header */}
      <section className="relative section-padding container-custom">
        <div className="max-w-3xl">
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">Our services</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02]">
            Everything your pet needs for a <span className="text-gradient-primary">perfect groom</span>.
          </h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            From quick clean-ups to full spa days, our certified groomers in Bhubaneswar use
            pet-safe products and a calm, careful approach. Pick a service or call us for advice.
          </p>
        </div>
      </section>

      {/* Service grid */}
      <section className="container-custom py-20 md:py-24 section-light">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="group relative rounded-3xl overflow-hidden border border-white/10 bg-[#2E0F94] card-lift"
              >
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 relative h-56 md:h-full min-h-[260px]">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1F0A66]/30" />
                    <div className="absolute top-4 left-4 bg-[#1F0A66]/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 text-xs text-white flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-[#C9BCFF]" /> {s.duration}
                    </div>
                  </div>
                  <div className="md:col-span-3 p-7 md:p-8 flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-[#FC5106]/15 border border-[#FC5106]/40 w-11 h-11 rounded-xl flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#FC5106]" />
                      </div>
                      <span className="text-xs text-white/40 font-mono">0{idx + 1}</span>
                    </div>
                    <h3 className="font-heading text-2xl md:text-[26px] font-bold text-white leading-tight mb-3">
                      {s.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed mb-5">{s.description}</p>

                    <ul className="space-y-2 mb-6">
                      {s.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2 text-sm text-white/70">
                          <CheckCircle2 className="w-4 h-4 text-[#C9BCFF] flex-shrink-0 mt-0.5" />
                          {h}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/10">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40">Starts at</p>
                        <p className="font-heading text-2xl font-bold text-white">
                          ₹{s.fromPrice}
                        </p>
                      </div>
                      <Link
                        to="/contact"
                        data-testid={`service-book-${s.id}`}
                        className="btn-primary !py-3 !px-5 !text-sm"
                      >
                        Book <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Home grooming callout */}
      <section className="container-custom pt-20 pb-28">
        <div className="relative rounded-[32px] overflow-hidden border border-white/10 grain">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/7516843/pexels-photo-7516843.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#3915AA] to-[#3915AA]/70" />
          </div>
          <div className="relative grid md:grid-cols-2 gap-8 items-center p-10 md:p-16">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/30 rounded-full px-4 py-2 mb-5">
                <HomeIcon className="w-4 h-4 text-[#C9BCFF]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#C9BCFF]">Home grooming</span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                We bring the studio <br />
                to your <span className="text-gradient-primary">doorstep</span>.
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-4 max-w-xl">
                Skip the carrier and the car ride. Our groomer arrives with portable bathing
                equipment, dryers and tools — perfect for senior pets, anxious dogs, or busy schedules.
                Available across Bhubaneswar.
              </p>
              <div className="inline-flex items-center gap-2 bg-white/15 border border-white/40 rounded-full px-4 py-2 mb-7">
                <Sparkles className="w-4 h-4 text-[#C9BCFF]" />
                <span className="text-sm text-white font-semibold">
                  No extra charge — pay studio prices at your doorstep
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" data-testid="home-grooming-cta" className="btn-primary">
                  Book home service <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" data-testid="home-grooming-pricing" className="btn-secondary">
                  See pricing
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: HomeIcon, t: "Doorstep", s: "We come to you" },
                { icon: Clock, t: "60–120 min", s: "Full session" },
                { icon: Sparkles, t: "All tools", s: "Carried by groomer" },
                { icon: CheckCircle2, t: "Stress-free", s: "Calm at home" },
              ].map((x) => (
                <div key={x.t} className="glass rounded-2xl p-5">
                  <x.icon className="w-5 h-5 text-[#FC5106] mb-3" />
                  <p className="text-white font-bold text-base">{x.t}</p>
                  <p className="text-white/60 text-sm">{x.s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
